import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { ICliente } from '@model/Cliente';
import { IClienteEvento } from '@model/ClienteEvento';
import { IComboClienteEventoTipo } from '@model/ClienteEventoTipo';
import { useClienteService } from '@services/ClienteService';
import { yup } from '@validations/Yup';
import React from 'react';
import { EventoRegistrarFormulario } from './EventoRegistrarFormulario';

export type EventoRegistrarModalProps = {
    modal: IModalWindow
    cliente: ICliente
    clienteEventoTipoCombo: IComboClienteEventoTipo
    onEventoRegistrado: (clienteEvento: IClienteEvento) => void
}

export function EventoRegistrarModal ({ modal, cliente, clienteEventoTipoCombo, onEventoRegistrado }: EventoRegistrarModalProps) {
    const tipoInputRef = React.useRef<HTMLInputElement>();

    const loading = useLoading();
    const message = useMessage();

    const form = useForm<Partial<IClienteEvento>>({
        clienteEventoTipoId: useField(),
        nota: useField()
    });

    const clienteService = useClienteService();

    const validation = useValidation<IClienteEvento>({
        clienteEventoTipoId: yup.number().required(),
        nota: yup.string().required()
    });

    const handleRegistrar = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(() => {
                    loading.start();
                    clienteService.registrarEvento(cliente.clienteId, values)
                        .then(response => {
                            message.success("Evento registrado com sucesso");
                            modal.close();
                            onEventoRegistrado(response.data);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [cliente.clienteId, clienteService, form, loading, message, modal, onEventoRegistrado, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoInputRef]);

    return (
        <ModalWindow 
            modal={modal} 
            size="sm" 
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle
                title="Registro de Evento do Cliente"
            />
            
            <ModalWindowContent>
                <EventoRegistrarFormulario 
                    form={form}
                    clienteEventoTipoCombo={clienteEventoTipoCombo}
                    tipoInputRef={tipoInputRef}
                />
            </ModalWindowContent>
            
            <ModalWindowActions>
                <PrimaryButton onClick={handleRegistrar}>Registrar Evento</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}